import * as React from 'react'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import styled from 'styled-components'
import { rem } from 'polished'
import LineClockDollarIcon from 'LuxKit/Icons/line/LineClockDollarIcon'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { getPaymentDueDate, isOfferDefaultDepositAmountPercentage } from 'lib/payment/depositsUtils'
import useToggle from 'hooks/useToggle'
import { DepositInfoModal } from 'components/Common/DepositInfo/DepositInfoModal'
import { depositAmountStringBuilder } from 'lib/tours/tourUtils'

import { getDefaultDepositAmountPercentage } from 'checkout/selectors/featureConfig/deposit'
import { useAppSelector } from 'hooks/reduxHooks'
import config from 'constants/config'
import { FormattedMessage, useIntl } from 'react-intl'
import { useContext } from 'react'
import GeoContext from 'contexts/geoContext'

const Container = styled.div`
  background-color: ${props => props.theme.palette.highlight.primary.lightBackground};
  border-radius: ${props => props.theme.borderRadius.M};
  display: flex;
  padding: ${rem(16)};
`

const RightSide = styled.div`
  margin-inline-start: ${rem(16)};

  ${mediaQueryUp.tablet} {
    display: flex;
    align-items: baseline;
  }
`

const StyledTextLink = styled(TextLink)`
  margin-inline-start: ${rem(8)};
`

interface Props {
  offerType: App.OfferType
  depositAmountPercentage: number
  depositThresholds?: App.DepositThreshold
  depositType?: App.Tours.V2OfferDepositType
  depositAmount?: number
}

export function SecureWithDepositBanner({ offerType, depositAmountPercentage, depositThresholds, depositType, depositAmount }: Props) {
  const intl = useIntl()
  const { currentCurrency, currentRegionCode } = useContext(GeoContext)
  const paymentDueDaysBeforeCheckin = getPaymentDueDate(depositThresholds, offerType)
  const [isPolicyOpen, , openPolicy, closePolicy] = useToggle()

  const defaultDepositAmountPercentage = useAppSelector(getDefaultDepositAmountPercentage)

  const isOfferUsingDefaultDepositPercentage = isOfferDefaultDepositAmountPercentage(depositThresholds, depositAmount)

  const depositAmountString = isOfferUsingDefaultDepositPercentage ?
    '' : !depositType ? ` ${depositAmountPercentage}%` : ` ${depositAmountStringBuilder(defaultDepositAmountPercentage, depositType, depositAmountPercentage, intl, currentCurrency, currentRegionCode).short}`

  if (!config.DEPOSITS_TAGS_ENABLED) {
    return null
  }

  return (
    <>
      <Container>
        <LineClockDollarIcon colour="primary" />
        <RightSide>
          <BodyText variant="large">
            <FormattedMessage
              id="6CJssymzwmFfJDs7afNT"
              defaultMessage="Secure with a <depositAmount>{depositAmountString} deposit</depositAmount> and pay the rest {days} days before your escape."
              
              values={{
                depositAmount: (chunks: React.ReactNode) => (
                  <BodyText as="span" variant="large" weight="bold" key="deposit">
                    {chunks}&nbsp;
                  </BodyText>
                ),
                depositAmountString,
                days: paymentDueDaysBeforeCheckin,
              }}
            />
          </BodyText>
          <StyledTextLink onClick={openPolicy} size="large">
            {intl.formatMessage({
              id: '5SWeZb0JGUQrTDGxSx0b',
              defaultMessage: 'Find out more',
            })}
          </StyledTextLink>
        </RightSide>
      </Container>
      <DepositInfoModal
        isModalOpen={isPolicyOpen}
        offerType={offerType}
        onCloseModal={closePolicy}
        depositAmountPercentage={depositAmountPercentage}
        depositThresholds={depositThresholds}
        depositType={depositType}
        depositAmount={depositAmount}
      />
    </>
  )
}
